import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Divider, Grid, Chip } from "@mui/material";

const COLORS = {
  label: "#475569",
  value: "#0f172a",
  accent: "#0369a1",
  border: "#e2e8f0",
  divider: "#f1f5f9",
  chipBg: "#e0f2fe",
  chipText: "#0369a1",
};

const formatDisplayDate = (value) => {
  if (!value || value === "null" || value === "undefined") {
    return null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleDateString();
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString();
  }
  return String(value);
};

const computeExpiringOnDate = (assignedDate, expirationDays, existingExpiresDate) => {
  const existing = formatDisplayDate(existingExpiresDate);
  if (existing) {
    return existing;
  }
  const days = parseInt(expirationDays, 10);
  if (!days || days <= 0 || Number.isNaN(days)) {
    return "N/A";
  }
  const base = assignedDate ? new Date(assignedDate) : new Date();
  if (Number.isNaN(base.getTime())) {
    return "N/A";
  }
  const expiring = new Date(base);
  expiring.setDate(expiring.getDate() + days);
  return expiring.toLocaleDateString();
};

const LabelValue = ({ label, value, mb = 0.75 }) => (
  <Typography variant="body2" sx={{ fontSize: "0.75rem", mb }}>
    <Box component="span" sx={{ fontWeight: 700, color: COLORS.label }}>
      {label}:
    </Box>{" "}
    <Box component="span" sx={{ color: COLORS.value, fontWeight: 500 }}>
      {value}
    </Box>
  </Typography>
);

LabelValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  mb: PropTypes.number,
};

const CodeChipValue = ({ label, value }) => (
  <React.Fragment>
    <Typography
      variant="body2"
      component="span"
      sx={{
        fontSize: "0.875rem",
        fontWeight: 700,
        color: COLORS.label,
        lineHeight: 1.2,
      }}
    >
      {label}:
    </Typography>
    <Chip
      label={value || "N/A"}
      size="small"
      sx={{
        justifySelf: "start",
        height: 28,
        fontWeight: 700,
        letterSpacing: "0.04em",
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        bgcolor: COLORS.chipBg,
        color: COLORS.chipText,
        border: `1px solid ${COLORS.accent}`,
        "& .MuiChip-label": {
          px: 1.25,
          fontSize: "0.8125rem",
        },
      }}
    />
  </React.Fragment>
);

CodeChipValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

const KioskDeliveryInfoPanel = ({
  t,
  stockCode,
  pickupCode,
  selectedBinId,
  locationControl,
  binSizeControl,
  addressLine1,
  addressLine2,
  city,
  state,
  zip,
  country,
  assignedDate,
  stockedDate,
  expiresDate,
  expirationDays,
}) => {
  const [binPulse, setBinPulse] = React.useState(false);
  const previousBinId = React.useRef(selectedBinId);

  React.useEffect(() => {
    if (selectedBinId && selectedBinId !== previousBinId.current) {
      setBinPulse(true);
      const timer = setTimeout(() => setBinPulse(false), 450);
      previousBinId.current = selectedBinId;
      return () => clearTimeout(timer);
    }
    previousBinId.current = selectedBinId;
    return undefined;
  }, [selectedBinId]);

  const resolvedAssignedDate =
    formatDisplayDate(assignedDate) || new Date().toLocaleDateString();
  const resolvedStockedDate = formatDisplayDate(stockedDate) || "N/A";
  const resolvedExpiringOn = computeExpiringOnDate(
    assignedDate || new Date(),
    expirationDays,
    expiresDate
  );
  const cityStateZip = [
    city && state ? `${city}, ${state}` : city || state || "",
    zip || "",
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const rowColumns = [
    locationControl ? "minmax(0, 1fr)" : null,
    binSizeControl ? "minmax(7.5rem, 0.9fr)" : null,
    "auto",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box sx={{ width: "100%", mt: 0.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: rowColumns,
          columnGap: 2.5,
          alignItems: "end",
          mb: 1.25,
          width: "100%",
        }}
      >
        {locationControl ? (
          <Box sx={{ minWidth: 0, width: "100%", pr: 0.5 }}>{locationControl}</Box>
        ) : null}
        {binSizeControl ? (
          <Box sx={{ minWidth: 0, width: "100%" }}>{binSizeControl}</Box>
        ) : null}
        <Box sx={{ minWidth: "7.5rem", pl: 1, overflow: "visible" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: "0.83rem",
              color: "#000000",
              lineHeight: 1.25,
              mb: "4px",
              letterSpacing: "0.01em",
            }}
          >
            {t("Assign.BIN")}
          </Typography>
          <Box
            className={selectedBinId ? "kiosk-bin-badge kiosk-bin-badge--active" : "kiosk-bin-badge"}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 28,
              px: 1.5,
              borderRadius: "6px",
              background: selectedBinId
                ? "linear-gradient(135deg, #0369a1 0%, #0284c7 100%)"
                : "#e2e8f0",
              border: selectedBinId ? "1px solid #075985" : "1px solid #cbd5e1",
              boxShadow: selectedBinId
                ? "0 1px 3px rgba(3, 105, 161, 0.25)"
                : "none",
              boxSizing: "border-box",
              overflow: "visible",
              transition: "background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
            }}
          >
            <Chip
              key={selectedBinId || "empty-bin"}
              label={selectedBinId || "—"}
              size="small"
              sx={{
                height: 20,
                fontWeight: 800,
                letterSpacing: "0.06em",
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                bgcolor: selectedBinId ? "#ffffff" : "transparent",
                color: selectedBinId ? "#0369a1" : "#64748b",
                animation: binPulse ? "binRibbonPop 0.45s ease-out" : "none",
                "@keyframes binRibbonPop": {
                  "0%": { transform: "scale(0.55)", opacity: 0 },
                  "55%": { transform: "scale(1.12)", opacity: 1 },
                  "100%": { transform: "scale(1)", opacity: 1 },
                },
                "& .MuiChip-label": {
                  px: 1,
                  fontSize: "0.8125rem",
                  lineHeight: 1,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          p: 1.5,
          background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
          borderRadius: 2,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "7.5rem minmax(0, 1fr)",
                columnGap: 1.25,
                rowGap: 1.25,
                alignItems: "center",
              }}
            >
              <CodeChipValue
                label={t("Assign.StockCode")}
                value={stockCode || "N/A"}
              />
              <CodeChipValue
                label={t("Assign.PUCode")}
                value={pickupCode || "N/A"}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  mb: 0.5,
                  color: COLORS.label,
                }}
              >
                Address
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  color: COLORS.value,
                  mb: 0.5,
                  fontWeight: 500,
                }}
              >
                {addressLine1 || "-"}
              </Typography>
              {addressLine2 ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: COLORS.value,
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  {addressLine2}
                </Typography>
              ) : null}
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  color: COLORS.value,
                  fontWeight: 500,
                }}
              >
                {cityStateZip || "-"}
              </Typography>
              {country ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: COLORS.value,
                    fontWeight: 500,
                    mt: 0.5,
                  }}
                >
                  {country}
                </Typography>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 1.25, borderColor: COLORS.border }} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          columnGap: 1.5,
        }}
      >
        <LabelValue
          label={t("Global.Prescriptions.Status.Assigned")}
          value={resolvedAssignedDate}
          mb={0}
        />
        <LabelValue
          label={t("Global.Prescriptions.Status.Stocked")}
          value={resolvedStockedDate}
          mb={0}
        />
        <LabelValue
          label={t("Assign.ExpiringOn", { defaultValue: "Expiring on" })}
          value={resolvedExpiringOn}
          mb={0}
        />
      </Box>
    </Box>
  );
};

KioskDeliveryInfoPanel.propTypes = {
  t: PropTypes.func.isRequired,
  stockCode: PropTypes.string,
  pickupCode: PropTypes.string,
  selectedBinId: PropTypes.string,
  locationControl: PropTypes.node,
  binSizeControl: PropTypes.node,
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
  country: PropTypes.string,
  assignedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  stockedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  expiresDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  expirationDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

KioskDeliveryInfoPanel.defaultProps = {
  stockCode: "",
  pickupCode: "",
  selectedBinId: "",
  locationControl: null,
  binSizeControl: null,
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  assignedDate: null,
  stockedDate: null,
  expiresDate: null,
  expirationDays: null,
};

export default KioskDeliveryInfoPanel;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/KioskDeliveryInfoPanel.jsx